import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SupportButton } from '../components/SupportButton';
import { LeadCapture, MaintenanceScreen, videoEmbed } from '../components/MaintenanceScreen';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Volleyball, ArrowRight, QrCode, Globe, CheckCircle2, Download,
  Trophy, Mountain, Compass, Users,
  BookOpen, GraduationCap, MonitorPlay, Headphones, BarChart3, Mic, Music,
  Check, HeartHandshake, Phone, Mail, Linkedin,
} from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const LEADS_OPEN = (() => {
  return new Date() <= new Date('2026-11-10T23:59:59+01:00');
})();

const ACT_ICONS = [Trophy, Mountain, Compass, Users];
const PILLAR_ICONS = [BookOpen, GraduationCap, MonitorPlay, Headphones, BarChart3, Mic];

function SectionTitle({ eyebrow, title, testid }) {
  return (
    <div className="mb-12 sm:mb-16">
      {eyebrow && (
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase mb-3">{eyebrow}</p>
      )}
      <h2 data-testid={testid} className="text-3xl md:text-5xl font-bold tracking-tight text-creme font-fredoka">
        {title}
      </h2>
    </div>
  );
}

export default function Landing() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [community, setCommunity] = useState(null);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const reduce = useReducedMotion();
  const reveal = (delay = 0) => reduce
    ? {}
    : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' }, transition: { ...spring, delay } };

  useEffect(() => {
    api.get('/giveback/count').then((r) => setCommunity(r.data)).catch(() => {});
    api.get('/settings').then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/check-code', { code: code.trim() });
      if (data.role === 'jeune') {
        navigate(`/inscription?code=${encodeURIComponent(code.trim())}`);
      } else {
        navigate(`/inscription-adulte?code=${encodeURIComponent(code.trim())}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('landing.codeInvalid'));
    } finally {
      setLoading(false);
    }
  };

  if (settings?.maintenance_mode === '1' && !authLoading && user?.role !== 'admin') {
    return <MaintenanceScreen settings={settings} />;
  }

  const videoUrl = (settings?.landing_video_url || '').trim();

  return (
    <div className="min-h-screen bg-nuit-900" data-testid="landing-page">
      <header className="sticky top-0 z-40 bg-nuit-900/80 backdrop-blur-xl border-b border-nuit-700/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 font-fredoka font-semibold text-xl text-or-400">
            <span className="w-10 h-10 shrink-0 rounded-full bg-or-400 flex items-center justify-center">
              <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
            </span>
            <span className="hidden sm:inline truncate">On croit qu'on joue !</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/connexion" data-testid="landing-login-link">
              <Button variant="ghost" className="rounded-full font-bold text-creme hover:bg-nuit-700 hover:text-or-300 focus:ring-2 focus:ring-or-400">
                {t('landing.login')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ---------- HERO : bannière officielle ---------- */}
      <section className="relative" data-testid="landing-hero">
        <h1 className="sr-only" data-testid="landing-hero-title">
          {t('landing.hero.title')} — {t('landing.hero.subtitle')} : {t('landing.hero.baseline')}
        </h1>
        <img src="/images/header_roman_projet.webp"
          alt="On croit qu'on joue ! — Le Prix du Rêve Sportif. Du rêve personnel à l'héritage collectif : un roman inspirant, des outils pédagogiques, des contenus audio et vidéo, des témoignages, des Rendez-vous OCQJ et une démarche Give Back."
          data-testid="landing-banner"
          className="w-full h-[260px] sm:h-[400px] lg:h-auto object-cover lg:object-contain bg-nuit-950" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-nuit-900 to-transparent pointer-events-none" />
      </section>

      {/* ---------- FORMULAIRE (leads ou code) + VIDÉO ---------- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className={videoUrl ? 'grid lg:grid-cols-2 gap-8 items-stretch' : ''}>
        <motion.div {...(reduce ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: spring })}
          className={`relative z-10 -mt-10 sm:-mt-16 ${videoUrl ? '' : 'max-w-2xl mx-auto'}`}>
          <div className="bg-nuit-800/90 backdrop-blur-xl border border-or-400/30 rounded-3xl p-6 sm:p-8 shadow-2xl gold-glow">
            <blockquote className="border-l-4 border-or-400/60 pl-4 text-base sm:text-lg text-creme/90 italic leading-relaxed mb-6"
              data-testid="landing-hero-quote">
              {settings?.content_landing_quote || t('landing.hero.quote')}
            </blockquote>
            {LEADS_OPEN ? (
              <LeadCapture t={t} />
            ) : (
            <form onSubmit={submit}>
              <label htmlFor="invite-code" className="flex items-center gap-2 text-sm font-bold text-creme mb-3">
                <QrCode className="w-4 h-4 text-or-400" strokeWidth={2.5} />
                {t('landing.codeLabel')}
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input id="invite-code" data-testid="invite-code-input" value={code}
                  onChange={(e) => setCode(e.target.value)} placeholder={t('landing.codePlaceholder')}
                  className="h-14 rounded-2xl text-base font-bold uppercase tracking-wider bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
                <Button type="submit" data-testid="invite-code-submit" disabled={loading}
                  className="h-14 rounded-full px-8 bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform gap-2">
                  {loading ? t('landing.checking') : t('landing.submit')}
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </Button>
              </div>
              <p className="text-xs text-brume mt-4">{t('landing.qrHint')}</p>
              <p className="text-sm mt-3">
                <Link to="/demande-acces" data-testid="request-access-link"
                  className="text-or-400 font-bold hover:text-or-300 hover:underline">
                  {t('landing.noCode')}
                </Link>
              </p>
            </form>
            )}
          </div>

          {!LEADS_OPEN && community && (community.givebacks > 0 || community.missions > 0) && (
            <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-or-300 bg-or-400/10 border border-or-400/30 rounded-full px-5 py-2"
              data-testid="community-counter">
              <Globe className="w-4 h-4" strokeWidth={2.5} />
              {t('landing.communityPrefix')} {[
                community.givebacks > 0 && `${community.givebacks} ${t(community.givebacks > 1 ? 'landing.givebackMany' : 'landing.givebackOne')}`,
                community.missions > 0 && `${community.missions} ${t(community.missions > 1 ? 'landing.missionMany' : 'landing.missionOne')}`,
              ].filter(Boolean).join(` ${t('landing.and')} `)} {t('landing.communitySuffix')}
            </p>
          )}
        </motion.div>
        {videoUrl && (
          <motion.div {...(reduce ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { ...spring, delay: 0.1 } })}
            className="relative z-10 -mt-10 sm:-mt-16" data-testid="landing-video">
            <div className="bg-nuit-800/90 backdrop-blur-xl border border-or-400/30 rounded-3xl p-4 sm:p-5 shadow-2xl gold-glow h-full flex flex-col">
              <h2 className="font-fredoka font-semibold text-xl text-creme mb-3 px-2">{t('landing.videoTitle')}</h2>
              {videoEmbed(videoUrl)}
            </div>
          </motion.div>
        )}
        </div>
      </div>

      {/* ---------- 4 ACTES ---------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24" data-testid="landing-acts">
        <SectionTitle title={t('landing.acts.title')} testid="landing-acts-title" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((n, i) => {
            const Icon = ACT_ICONS[i];
            return (
              <motion.article key={n} {...reveal(i * 0.1)} data-testid={`landing-act-${n}`}
                className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 hover:-translate-y-1 hover:border-or-400/40 hover:shadow-[0_0_15px_rgba(232,197,102,0.3)] transition-[transform,border-color,box-shadow]">
                <div className="flex items-center gap-4 mb-5">
                  <span className="w-12 h-12 rounded-full bg-or-400/10 border border-or-400/40 flex items-center justify-center text-or-400 font-fredoka font-bold text-xl">
                    {n}
                  </span>
                  <Icon className="w-6 h-6 text-or-400" strokeWidth={2.5} />
                </div>
                <h3 className="text-xl font-semibold text-creme mb-2 font-fredoka">{t(`landing.acts.${n}.title`)}</h3>
                <p className="text-base text-brume leading-relaxed">{t(`landing.acts.${n}.desc`)}</p>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* ---------- BANDEAU ACTION ---------- */}
      <motion.div {...reveal(0)} className="bg-or-400 py-6 overflow-hidden" data-testid="landing-action-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-2 text-nuit-950 font-fredoka font-bold uppercase tracking-widest text-lg sm:text-2xl">
          {[1, 2, 3, 4].map((n) => (
            <span key={n} className="flex items-center gap-8">
              <span>{t(`landing.action_band.${n}`)}</span>
              {n < 4 && <ArrowRight className="w-6 h-6 shrink-0" strokeWidth={3} />}
            </span>
          ))}
        </div>
      </motion.div>

      {/* ---------- 6 PILIERS ---------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24" data-testid="landing-pillars">
        <SectionTitle title={t('landing.pillars.title')} testid="landing-pillars-title" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((n, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <motion.article key={n} {...reveal(i * 0.08)} data-testid={`landing-pillar-${n}`}
                className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8 hover:-translate-y-1 hover:border-or-400/40 transition-[transform,border-color]">
                <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-or-400" strokeWidth={2.5} />
                </span>
                <h3 className="text-lg sm:text-xl font-semibold text-creme mb-2 font-fredoka">{t(`landing.pillars.${n}.title`)}</h3>
                <p className="text-sm sm:text-base text-brume leading-relaxed">{t(`landing.pillars.${n}.desc`)}</p>
              </motion.article>
            );
          })}
        </div>

        <motion.div {...reveal(0.25)} data-testid="landing-audio" className="mt-12 sm:mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-creme font-fredoka" data-testid="landing-audio-title">{t('landing.audio.title')}</h3>
            <p className="text-base text-brume leading-relaxed mt-3 max-w-2xl mx-auto" data-testid="landing-audio-desc">{t('landing.audio.desc')}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[1, 2].map((n) => {
              const src = (settings?.[`landing_audio${n}_url`] || '').trim() || `/assets/musique-${n}.mp3`;
              return (
                <div key={n} data-testid={`landing-audio-${n}`}
                  className="bg-nuit-800 border border-or-400/30 rounded-3xl p-6 gold-glow">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-10 h-10 rounded-full bg-or-400 flex items-center justify-center shrink-0">
                      <Music className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
                    </span>
                    <h4 className="text-lg font-bold text-creme font-fredoka" data-testid={`landing-audio-title-${n}`}>
                      {(settings?.[`landing_audio${n}_title`] || '').trim() || t(`landing.audio.track${n}`)}
                    </h4>
                  </div>
                  {src ? (
                    <audio controls preload="none" src={src} data-testid={`landing-audio-player-${n}`} className="w-full" />
                  ) : (
                    <p className="text-sm text-brume italic" data-testid={`landing-audio-soon-${n}`}>{t('landing.audio.soon')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* ---------- MASTERCLASS : 4 RENDEZ-VOUS EN LIGNE ---------- */}
      {settings?.page_masterclass !== '0' && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24" data-testid="landing-mc">
          <SectionTitle title={t('landing.mc.title')} testid="landing-mc-title" />
          <p className="text-center text-base md:text-lg text-brume leading-relaxed -mt-8 mb-12 max-w-3xl mx-auto" data-testid="landing-mc-subtitle">{t('landing.mc.subtitle')}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((n) => (
              <motion.div key={n} {...reveal(0.08 * n)} data-testid={`landing-mc-card-${n}`}
                className="bg-nuit-800 border border-or-400/30 rounded-3xl p-6 gold-glow flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 rounded-full bg-or-400 flex items-center justify-center font-fredoka font-bold text-nuit-950 text-lg shrink-0">{n}</span>
                  <p className="text-sm font-bold text-or-300" data-testid={`landing-mc-date-${n}`}>{(settings?.[`mc_ev${n}_date`] || '').trim() || t(`landing.mc.ev${n}.date`)}</p>
                </div>
                <h3 className="font-fredoka font-semibold text-xl text-creme mb-2" data-testid={`landing-mc-audience-${n}`}>{(settings?.[`mc_ev${n}_audience`] || '').trim() || t(`landing.mc.ev${n}.audience`)}</h3>
                <p className="text-sm text-brume leading-relaxed mb-4 flex-1" data-testid={`landing-mc-objective-${n}`}>{(settings?.[`mc_ev${n}_objective`] || '').trim() || t(`landing.mc.ev${n}.objective`)}</p>
                <p className="text-sm font-bold text-creme bg-nuit-900 border border-or-400/20 rounded-2xl px-4 py-3" data-testid={`landing-mc-subject-${n}`}>« {(settings?.[`mc_ev${n}_subject`] || '').trim() || t(`landing.mc.ev${n}.subject`)} »</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal(0.4)} data-testid="landing-mc-zoom"
            className="mt-10 bg-nuit-800 border border-or-400/30 rounded-3xl p-6 sm:p-8 gold-glow flex flex-col md:flex-row items-center gap-6">
            <img src="/images/flyer_masterclass.png" alt={t('landing.mc.title')} data-testid="landing-mc-flyer"
              className="w-28 rounded-2xl border border-nuit-600 shrink-0" />
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-fredoka font-semibold text-xl text-creme mb-1">{t('landing.mc.zoomTitle')}</h3>
              <p className="text-sm text-brume" data-testid="landing-mc-zoom-id">{t('landing.mc.zoomId')}</p>
            </div>
            <a href="https://us02web.zoom.us/j/85693038418?pwd=Dmea8lAb7x7g8AJqcbMYTFOLPz74dz.1"
              target="_blank" rel="noopener noreferrer" data-testid="landing-mc-cta">
              <Button className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold h-12 px-8 shadow-md hover:-translate-y-0.5 transition-transform gap-2">
                {t('landing.mc.cta')} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Button>
            </a>
          </motion.div>
        </section>
      )}

      {/* ---------- ÉCOSYSTÈME (BENTO) ---------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 sm:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <motion.article {...reveal(0)} data-testid="landing-masterclass-card"
            className="md:col-span-7 md:row-span-2 bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 hover:border-or-400/40 transition-[border-color]">
            <p className="text-or-400 font-bold tracking-widest text-sm uppercase mb-3">{t('landing.masterclass.subtitle')}</p>
            <h3 className="text-2xl md:text-3xl font-bold text-creme font-fredoka mb-8">{t('landing.masterclass.title')}</h3>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <li key={n} className="flex items-start gap-3 text-base text-creme/90">
                  <Check className="w-5 h-5 text-or-400 shrink-0 mt-0.5" strokeWidth={3} />
                  {t(`landing.masterclass.points.${n}`)}
                </li>
              ))}
            </ul>
            <Link to="/masterclass" data-testid="landing-masterclass-link"
              className="mt-10 inline-flex items-center gap-2 text-or-400 font-bold hover:text-or-300 hover:underline">
              {t('footer.masterclass')}
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <img src="/images/masterclass.png" alt="Dimitri M'Foumou-Titi, auteur d'On croit qu'on joue !, bras croisés devant le stade — Rendez-vous OCQJ !"
              data-testid="landing-masterclass-photo"
              className="mt-8 w-full h-48 sm:h-56 object-cover object-[75%_15%] rounded-2xl ring-1 ring-or-400/20" />
          </motion.article>

          <motion.article {...reveal(0.1)} data-testid="landing-crowdfunding-card"
            className="md:col-span-5 bg-nuit-800 border border-or-400/30 rounded-3xl p-8 hover:shadow-[0_0_15px_rgba(232,197,102,0.3)] transition-[box-shadow]">
            <p className="text-or-400 font-bold tracking-widest text-sm uppercase mb-3">{t('landing.crowdfunding.subtitle')}</p>
            <h3 className="text-2xl font-bold text-creme font-fredoka mb-4">{t('landing.crowdfunding.title')}</h3>
            <p className="text-base text-brume leading-relaxed mb-5">{t('landing.crowdfunding.desc')}</p>
            <ul className="space-y-3 mb-8">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex items-start gap-3 text-base text-creme/90">
                  <Check className="w-5 h-5 text-or-400 shrink-0 mt-0.5" strokeWidth={3} />
                  {t(`landing.crowdfunding.points.${n}`)}
                </li>
              ))}
            </ul>
            <SupportButton />
          </motion.article>

          <motion.article {...reveal(0.15)} data-testid="landing-giveback-card"
            className="md:col-span-5 bg-nuit-800 border border-nuit-700 rounded-3xl p-8 hover:border-or-400/40 transition-[border-color]">
            <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center mb-5">
              <HeartHandshake className="w-6 h-6 text-or-400" strokeWidth={2.5} />
            </span>
            <h3 className="text-2xl font-bold text-creme font-fredoka mb-3">{t('landing.giveback.title')}</h3>
            <p className="text-base text-brume leading-relaxed">{t('landing.giveback.desc')}</p>
          </motion.article>

          <motion.article {...reveal(0.2)} data-testid="landing-batisseurs-card"
            className="md:col-span-12 bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 hover:border-or-400/40 transition-[border-color]">
            <div className="flex-1">
              <h3 className="text-2xl md:text-3xl font-bold text-creme font-fredoka mb-3">{t('landing.batisseurs.title')}</h3>
              <p className="text-base sm:text-lg text-brume leading-relaxed max-w-2xl">{t('landing.batisseurs.desc')}</p>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link to="/demande-acces" data-testid="landing-batisseurs-cta">
                <Button className="h-14 w-full rounded-full px-8 bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform gap-2 whitespace-nowrap">
                  {t('landing.batisseurs.cta')}
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                </Button>
              </Link>
              <a href="/docs/dossier-partenariat.pdf" download="on-croit-qu-on-joue_dossier-partenariat.pdf"
                data-testid="landing-batisseurs-dossier"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-or-400/60 text-or-300 font-bold text-sm px-6 py-3 hover:bg-or-400/10 transition-colors whitespace-nowrap">
                <Download className="w-4 h-4" strokeWidth={2.5} /> {t('landing.batisseurs.dossier')}
              </a>
            </div>
          </motion.article>
        </div>
      </section>

      {/* ---------- AUTEUR ---------- */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center" data-testid="landing-author-block">
        <motion.div {...reveal(0)}>
          <p className="text-xl sm:text-2xl text-creme/90 italic leading-relaxed mb-10" data-testid="landing-author-quote">
            {t('landing.author.quote')}
          </p>
          <h3 className="text-2xl md:text-3xl font-bold text-or-400 font-fredoka" data-testid="landing-author-name">
            {t('landing.author.name')}
          </h3>
          <p className="text-base text-brume mt-2 mb-8" data-testid="landing-author-role">{t('landing.author.role')}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+33698697558" data-testid="landing-author-tel"
              className="inline-flex items-center gap-2 rounded-full border border-nuit-600 bg-nuit-800 px-5 py-2.5 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors focus:ring-2 focus:ring-or-400">
              <Phone className="w-4 h-4 text-or-400" strokeWidth={2.5} />
              +33 6 98 69 75 58
            </a>
            <a href="mailto:dimitri.mfoumou@gmail.com" data-testid="landing-author-email"
              className="inline-flex items-center gap-2 rounded-full border border-nuit-600 bg-nuit-800 px-5 py-2.5 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors focus:ring-2 focus:ring-or-400">
              <Mail className="w-4 h-4 text-or-400" strokeWidth={2.5} />
              dimitri.mfoumou@gmail.com
            </a>
            <a href="https://www.linkedin.com/search/results/all/?keywords=Dimitri%20M%27Foumou-Titi"
              target="_blank" rel="noopener noreferrer" data-testid="landing-author-linkedin"
              className="inline-flex items-center gap-2 rounded-full border border-nuit-600 bg-nuit-800 px-5 py-2.5 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors focus:ring-2 focus:ring-or-400">
              <Linkedin className="w-4 h-4 text-or-400" strokeWidth={2.5} />
              LinkedIn
            </a>
          </div>
        </motion.div>
      </section>

      {/* ---------- BANDEAU FINAL ---------- */}
      <section className="relative border-t border-nuit-700 overflow-hidden" data-testid="landing-closing-band">
        <img src="/images/closing_bg.jpg" alt="" aria-hidden="true"
          data-testid="landing-closing-bg"
          className="absolute inset-0 w-full h-full object-cover object-[25%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-nuit-900/60 via-nuit-900/80 to-nuit-900/95" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center">
          <motion.h2 {...reveal(0)}
            className="text-3xl sm:text-5xl font-bold tracking-tight text-creme leading-tight font-fredoka [text-shadow:0_2px_20px_rgba(5,13,24,0.9)]"
            data-testid="landing-closing-text">
            {settings?.content_landing_closing || t('landing.closing')}
          </motion.h2>
          <motion.div {...reveal(0.1)} className="mt-10 inline-block">
            <SupportButton />
          </motion.div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="border-t border-nuit-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-center text-xs text-brume mb-8" data-testid="landing-security-note">
            {t('landing.security')}
          </p>
          <div className="flex flex-wrap gap-6 justify-center text-sm font-bold text-brume">
            {[
              { to: '/le-livre', key: 'page_livre', label: t('footer.book'), tid: 'landing-footer-livre' },
              { to: '/medias', key: 'page_medias', label: t('footer.media'), tid: 'landing-footer-medias' },
              { to: '/masterclass', key: 'page_masterclass', label: t('footer.masterclass'), tid: 'landing-footer-masterclass' },
              { to: '/a-propos', key: 'page_apropos', label: t('footer.about'), tid: 'landing-footer-apropos' },
              { to: '/faq', key: 'page_faq', label: t('footer.faq'), tid: 'landing-footer-faq' },
              { to: '/confidentialite', key: null, label: t('footer.privacy'), tid: 'landing-footer-privacy' },
              { to: '/mentions-legales', key: null, label: t('footer.legal'), tid: 'landing-footer-legal' },
              { to: '/presse', key: 'page_presse', label: t('footer.press'), tid: 'landing-footer-press' },
              { to: '/contact', key: 'page_contact', label: t('footer.contact'), tid: 'landing-footer-contact' },
              { to: '/demande-acces', key: null, label: t('footer.access'), tid: 'landing-footer-acces' },
            ].filter((l) => !l.key || !settings || settings[l.key] !== '0').map((l) => (
              <Link key={l.tid} to={l.to} className="hover:text-or-300" data-testid={l.tid}>{l.label}</Link>
            ))}
            <SupportButton variant="link" testid="support-project-link" />
          </div>
        </div>
      </footer>
    </div>
  );
}
